import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaPlus, FaTrash, FaSave } from 'react-icons/fa';
import { useApp } from '../../context/AppContext';
import {
  getAdminTicketStatuses,
  createTicketStatus,
  updateTicketStatus,
  deleteTicketStatus,
} from '../../services/ticketStatusService';
import {
  STATUS_CATEGORY_OPTIONS,
  formatDemandTypesShort,
} from '../../services/statusService';
import { DEMAND_TYPE_FILTER_OPTIONS } from '../../services/demandTypeService';
import AdminSettingsBackLink from '../../components/admin/AdminSettingsBackLink';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  background-color: var(--color-bg-secondary);
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Panel = styled.section`
  background: var(--color-bg-primary);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid var(--color-border-primary);
  box-shadow: var(--shadow-sm);
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PageTitle = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.5rem;
  font-weight: 650;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--color-text-primary);
`;

const Hint = styled.p`
  margin: 0 0 1.25rem;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
`;

const inputStyles = `
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--color-border-primary);
  border-radius: 10px;
  font-size: 0.9rem;
  font-family: inherit;
  color: var(--color-text-primary);
  background: var(--color-bg-primary);
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: var(--color-secondary);
    box-shadow: 0 0 0 2px rgba(178, 190, 195, 0.35);
  }

  &:disabled {
    background: var(--color-light);
    color: var(--color-text-muted);
    cursor: not-allowed;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-text-secondary);
`;

const StyledInput = styled.input`
  ${inputStyles}
`;

const StyledSelect = styled.select`
  ${inputStyles}
  cursor: pointer;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  width: 100%;
  max-width: 100%;
`;

const Table = styled.table`
  width: 100%;
  min-width: 960px;
  border-collapse: collapse;
  font-size: 0.9rem;

  th,
  td {
    padding: 0.75rem 0.5rem;
    text-align: left;
    border-bottom: 1px solid var(--color-border-primary);
    vertical-align: middle;
  }

  th {
    color: var(--color-text-secondary);
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    background: var(--color-light);
  }

  tbody tr:hover {
    background: var(--color-hover-overlay);
  }
`;

const TableInput = styled.input`
  ${inputStyles}
  min-width: 120px;
`;

const TableSelect = styled.select`
  ${inputStyles}
  min-width: 160px;
  cursor: pointer;
`;

const DemandTypeGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.75rem;
  min-width: 200px;
`;

const DemandTypeChip = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.78rem;
  color: var(--color-text-primary);
  cursor: pointer;
  padding: 0.2rem 0.45rem;
  border-radius: 8px;
  background: ${({ $checked }) => ($checked ? 'var(--color-bg-tertiary)' : 'var(--color-light)')};
  border: 1px solid ${({ $checked }) => ($checked ? 'var(--color-border-accent)' : 'var(--color-border-primary)')};

  input {
    accent-color: var(--color-border-accent);
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 0.9rem;
  border: 1px solid ${({ $variant }) =>
    $variant === 'danger'
      ? '#ef4444'
      : $variant === 'secondary'
        ? 'var(--color-border-primary)'
        : 'var(--color-btn-primary-border)'};
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  background: ${({ $variant }) =>
    $variant === 'danger'
      ? 'transparent'
      : $variant === 'secondary'
        ? 'var(--color-bg-tertiary)'
        : 'var(--color-btn-primary-bg)'};
  color: ${({ $variant }) =>
    $variant === 'danger'
      ? '#ef4444'
      : $variant === 'secondary'
        ? 'var(--color-text-primary)'
        : 'var(--color-btn-primary-text)'};
  white-space: nowrap;
  box-sizing: border-box;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Badge = styled.span`
  display: inline-block;
  font-size: 0.68rem;
  padding: 0.15rem 0.45rem;
  border-radius: 6px;
  margin-left: 0.35rem;
  background: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border-primary);
`;

const ActionsWrap = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

const CodeCell = styled.td`
  font-family: ui-monospace, monospace;
  font-size: 0.85rem;
`;

const emptyForm = () => ({
  code: '',
  label: '',
  category: 'IN_PROGRESS',
  sortOrder: 100,
  demandTypes: DEMAND_TYPE_FILTER_OPTIONS.map((option) => option.value),
});

const DemandTypeSelector = ({ selected, onChange, compact = false }) => (
  <DemandTypeGrid>
    {DEMAND_TYPE_FILTER_OPTIONS.map((opt) => {
      const checked = selected.includes(opt.value);
      return (
        <DemandTypeChip key={opt.value} $checked={checked}>
          <input
            type="checkbox"
            checked={checked}
            onChange={() => {
              if (checked) {
                const next = selected.filter((v) => v !== opt.value);
                if (next.length > 0) onChange(next);
              } else {
                onChange([...selected, opt.value]);
              }
            }}
          />
          {compact ? opt.value.replace('_', ' ') : opt.label}
        </DemandTypeChip>
      );
    })}
  </DemandTypeGrid>
);

const TicketStatusesAdminPage = () => {
  const { reloadTicketStatuses, addToast } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminTicketStatuses();
      setItems(
        (Array.isArray(data) ? data : []).map((row) => ({
          ...row,
          demandTypes: Array.isArray(row.demandTypes) && row.demandTypes.length > 0
            ? row.demandTypes.map((t) => String(t).toUpperCase())
            : DEMAND_TYPE_FILTER_OPTIONS.map((option) => option.value),
        }))
      );
    } catch (err) {
      console.error(err);
      addToast?.('Erro ao carregar status.', 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshAll = async () => {
    await load();
    await reloadTicketStatuses?.();
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.demandTypes?.length) {
      addToast?.('Selecione pelo menos um tipo de demanda.', 'error');
      return;
    }
    setSaving(true);
    try {
      await createTicketStatus({
        code: form.code,
        label: form.label,
        category: form.category,
        sortOrder: Number(form.sortOrder) || 0,
        demandTypes: form.demandTypes,
      });
      addToast?.('Status criado.', 'success');
      setForm(emptyForm());
      await refreshAll();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Erro ao criar status.';
      addToast?.(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRowUpdate = async (row) => {
    if (!row.demandTypes?.length) {
      addToast?.('Selecione pelo menos um tipo de demanda.', 'error');
      return;
    }
    try {
      await updateTicketStatus(row.id, {
        label: row.label,
        category: row.category,
        sortOrder: Number(row.sortOrder) || 0,
        active: row.active,
        demandTypes: row.demandTypes,
      });
      addToast?.('Status atualizado.', 'success');
      await refreshAll();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Erro ao atualizar.';
      addToast?.(msg, 'error');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remover o status "${row.label}" (${row.code})?`)) return;
    try {
      await deleteTicketStatus(row.id);
      addToast?.('Status removido.', 'success');
      await refreshAll();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Não foi possível remover.';
      addToast?.(msg, 'error');
    }
  };

  const patchRow = (id, field, value) => {
    setItems((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  return (
    <PageContainer>
      <AdminSettingsBackLink />
      <Panel>
        <PageTitle>Configurar status de tickets</PageTitle>
        <Hint>
          Defina quais status existem para cada tipo de demanda (Suporte/Bug, Melhoria, Projeto, etc.).
          A categoria controla o comportamento (pendência, resolução, bloqueio). Status em uso só podem ser
          desativados, não removidos.
        </Hint>

        <form onSubmit={handleCreate}>
          <FormGrid>
            <FieldLabel>
              Código
              <StyledInput
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="EX.: EM_ANALISE"
                required
              />
            </FieldLabel>
            <FieldLabel>
              Rótulo
              <StyledInput
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="Em análise"
                required
              />
            </FieldLabel>
            <FieldLabel>
              Categoria
              <StyledSelect
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              >
                {STATUS_CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </StyledSelect>
            </FieldLabel>
            <FieldLabel>
              Ordem
              <StyledInput
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
              />
            </FieldLabel>
          </FormGrid>

          <FieldLabel style={{ marginBottom: '1rem' }}>
            Tipos de demanda
            <DemandTypeSelector
              selected={form.demandTypes}
              onChange={(types) => setForm((f) => ({ ...f, demandTypes: types }))}
            />
          </FieldLabel>

          <Button type="submit" disabled={saving}>
            <FaPlus />
            Adicionar status
          </Button>
        </form>
      </Panel>

      <Panel>
        {loading ? (
          <p>Carregando...</p>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Rótulo</th>
                  <th>Categoria</th>
                  <th>Tipos de demanda</th>
                  <th>Ordem</th>
                  <th>Ativo</th>
                  <th>Tickets</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id}>
                    <CodeCell>
                      {row.code}
                      {row.system && <Badge $system>sistema</Badge>}
                    </CodeCell>
                    <td>
                      <TableInput
                        value={row.label}
                        onChange={(e) => patchRow(row.id, 'label', e.target.value)}
                      />
                    </td>
                    <td>
                      <TableSelect
                        value={row.category}
                        onChange={(e) => patchRow(row.id, 'category', e.target.value)}
                      >
                        {STATUS_CATEGORY_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </TableSelect>
                    </td>
                    <td>
                      <DemandTypeSelector
                        compact
                        selected={row.demandTypes}
                        onChange={(types) => patchRow(row.id, 'demandTypes', types)}
                      />
                      <div
                        style={{
                          fontSize: '0.72rem',
                          color: 'var(--color-text-muted)',
                          marginTop: '0.35rem',
                        }}
                      >
                        {formatDemandTypesShort(row.demandTypes)}
                      </div>
                    </td>
                    <td>
                      <TableInput
                        type="number"
                        style={{ maxWidth: 80 }}
                        value={row.sortOrder}
                        onChange={(e) => patchRow(row.id, 'sortOrder', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={row.active}
                        disabled={row.system && row.code === 'OPEN'}
                        onChange={(e) => patchRow(row.id, 'active', e.target.checked)}
                        style={{ width: 18, height: 18, accentColor: '#28a745' }}
                      />
                      {!row.active && <Badge $inactive>inativo</Badge>}
                    </td>
                    <td>{row.ticketCount ?? 0}</td>
                    <td>
                      <ActionsWrap>
                        <Button type="button" $variant="secondary" onClick={() => handleRowUpdate(row)}>
                          <FaSave />
                          Salvar
                        </Button>
                        {!row.system && (
                          <Button type="button" $variant="danger" onClick={() => handleDelete(row)}>
                            <FaTrash />
                          </Button>
                        )}
                      </ActionsWrap>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Panel>
    </PageContainer>
  );
};

export default TicketStatusesAdminPage;
